import { isEqual } from 'lodash';
import { getDefaultLazyStateFilters } from '../../../utils/getDefaultLazyStateFilters';
import DetailedFilter from './components/DetailedFilter/DetailedFilter';
import { Dataset, ReferenceControl } from '../../../types/types';
import { useDetailFilterContext } from '../../../hooks/context/useDetailFilterContext';
import { useSelectedFiltersContext } from '../../../hooks/context/useSelectedFiltersContext';
import { useDatasetContext } from '../../../hooks/context/useDatasetContext';
import { getCorrectSubmitFilters } from './utils/getCorrectSubmitFilters';
import { useSelectedRowsContext } from '../../../hooks/context/useSelectedRowsContext';
import { useSlaveDatasetContext } from '../../../hooks/context/useSlaveDatasetContext';
import { useMemo } from 'react';
import { getSubsetDataApiRequestData } from '../../../api/utils/requestData/getSubsetDataApiRequestData';
import { getDataKey } from '../../../utils/getDataKey';
import { useUserState } from '../../../hooks/context/useUserState';
import { getFilterParamObject } from '../../../utils/apiRequests/getFilterParamObject';
import RequestDataProvider from '../../../context/RequestDataContext';
import { updateActiveFiltersFromLazyState } from '../../../utils/filters/updateActiveFiltersFromLazyState';

interface SlaveFilterProps {
  subsetId: string;
  closeFilter: () => void;
  openFullscreenReference: (
    name: string,
    datasetId: string,
    control: ReferenceControl,
    value: string,
    label: string,
    resolvedFilter?: Record<string, any> | null
  ) => void;
}

const SlaveFilter = ({
  subsetId,
  closeFilter,
  openFullscreenReference,
}: SlaveFilterProps) => {
  const { setDefaultSlavePopoverFilters, submitSlavePopoverFilters } =
    useDetailFilterContext();

  const {
    state: { token: sessionToken },
  } = useUserState();

  const {
    selectedFilters,
    setSelectedFilters,
    popoverFilters,
    setPopoverFilters,
  } = useSelectedFiltersContext();

  const { resetSlaveSelectedRows, expandedRow } = useSelectedRowsContext();

  const {
    referenceDatasets,
    slaveFilters,
    slaveDatasets,
    setReferenceDatasets,
    slaveLazyStates,
    setSlaveLazyStates,
  } = useDatasetContext();

  const { activeFilters } = useSlaveDatasetContext();

  const dataset: Dataset | undefined = slaveDatasets[subsetId];
  const filter = slaveFilters[subsetId];
  const dataKey = getDataKey(dataset);

  const requestData = useMemo(
    () =>
      dataset
        ? getSubsetDataApiRequestData(
            expandedRow?.[dataKey],
            dataset,
            [],
            sessionToken,
            0,
            getFilterParamObject(
              slaveLazyStates?.[dataset.datasetId]?.['filters'] as Record<
                string,
                any
              > | null
            )
          )
        : undefined,
    [sessionToken, dataset, dataKey, expandedRow]
  );

  const submitFilters = (filters: any = undefined) => {
    const newFilters = getDefaultLazyStateFilters(dataset, filter);
    getCorrectSubmitFilters(dataset, filters, selectedFilters, newFilters);

    // Compare API-level active filters, not raw lazy state: PrimeReact column filters write a different shape, so a raw isEqual gives false negatives and reloads the slave grid.
    const incomingActiveFilters = updateActiveFiltersFromLazyState(
      null,
      { filters: newFilters },
      dataset
    );
    const hasEqualFilters = isEqual(activeFilters, incomingActiveFilters);
    if (!hasEqualFilters) {
      resetSlaveSelectedRows();
    }

    setSlaveLazyStates((oldLazyState: any) => {
      if (hasEqualFilters) {
        return oldLazyState;
      }

      const newSlaveLazyState = {
        ...oldLazyState[dataset.datasetId],
        filters: newFilters,
      };

      return { ...oldLazyState, [dataset.datasetId]: newSlaveLazyState };
    });

    closeFilter();
  };

  return (
    <RequestDataProvider requestData={requestData}>
      <DetailedFilter
        submitFilters={submitFilters}
        filter={filter}
        popoverFilters={popoverFilters}
        setPopoverFilters={setPopoverFilters}
        selectedFilters={selectedFilters}
        setSelectedFilters={setSelectedFilters}
        setDefaultPopoverFilters={setDefaultSlavePopoverFilters}
        submitPopoverFilters={submitSlavePopoverFilters}
        isSlaveFilter
        dataset={dataset}
        referenceDatasets={referenceDatasets}
        setReferenceDatasets={setReferenceDatasets}
        openFullscreenReference={openFullscreenReference}
      />
    </RequestDataProvider>
  );
};

export default SlaveFilter;
